import { shuffle } from 'lodash-es';
import { useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { FabiJobSearchQueryGenerationUseCase } from '@xing-com/crate-common-graphql-types';
import { useTracking } from '@xing-com/crate-communication-tracking';
import { navigate } from '@xing-com/crate-core-router';
import { XING_JOBS_SEARCH_KI_PATH } from '@xing-com/crate-core-routes';
import { useMatchMedia } from '@xing-com/crate-hooks-use-match-media';
import { AI_SEARCH_INIT_PROMPT_PARAM } from '@xing-com/crate-jobs-constants';
import { useLoginAppUrl } from '@xing-com/crate-xinglet';
import {
  IconAICareerChanger,
  IconAIProfileSearch,
  IconArrowLeft,
  IconArrowRight,
  IconSalary,
} from '@xing-com/icons';

import { line1, line2 } from './data';
import {
  StyledKeywords,
  StyledTag,
  StyledLink,
  AiTag,
  AiTagContent,
  StyledLeftButton,
  StyledRightButton,
} from './keywords.styled';

type Props = {
  isStartPage?: boolean;
};

export const Keywords: React.FC<Props> = ({ isStartPage }) => {
  const { track } = useTracking();
  const [isMouseDown, setIsMouseDown] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [keywordsPrepared, setKeywordsPrepared] = useState(false);
  const [keywordsData, setKeywordsData] = useState({
    line1,
    line2,
  });
  const loginAppUrl = useLoginAppUrl();
  const keywordsRef = useRef<HTMLDivElement>(null);
  const mouseCoords = useRef({
    startX: 0,
    scrollLeft: 0,
  });
  const isTouchDevice = useMatchMedia('(pointer: coarse)');

  const getSuggestionUrl = (
    useCase: FabiJobSearchQueryGenerationUseCase
  ): string => {
    const params = new URLSearchParams({
      [AI_SEARCH_INIT_PROMPT_PARAM]: useCase,
    });
    if (isStartPage) {
      params.set('signup_channel', 'minireg_losp_swimp_swims');
    }

    return `${loginAppUrl}?dest_url=${encodeURIComponent(`${XING_JOBS_SEARCH_KI_PATH}?${params.toString()}`)}`;
  };

  const handleDragStart = (event: React.MouseEvent<HTMLDivElement>): void => {
    if (!keywordsRef.current) return;
    const keywords = keywordsRef.current;
    const startX = event.pageX - keywords.offsetLeft;
    const scrollLeft = keywords.scrollLeft;

    mouseCoords.current = { startX, scrollLeft };
    setIsMouseDown(true);
    document.body.style.cursor = 'grabbing';
  };

  const handleDragEnd = (): void => {
    setIsMouseDown(false);
    if (!keywordsRef.current) return;
    document.body.style.cursor = 'default';
    setIsDragging(false);
  };

  const handleDrag = (event: React.MouseEvent<HTMLDivElement>): void => {
    if (!isMouseDown || !keywordsRef.current) return;
    event.preventDefault();

    const keywords = keywordsRef.current;
    const currentX = event.pageX - keywords.offsetLeft;
    const distanceX = (currentX - mouseCoords.current.startX) * 1.5;

    keywords.scrollLeft = mouseCoords.current.scrollLeft - distanceX;
    if (distanceX > 2) setIsDragging(true);
  };

  const handleClick =
    (pill: 'swimp' | 'swims') =>
    (event: React.MouseEvent<HTMLAnchorElement>): void => {
      if (isStartPage) {
        event.preventDefault();

        track({
          type: 'nwt',
          application: 'wbm',
          sentBy: 'welcome',
          event: 'opened',
          element:
            pill === 'swimp'
              ? 'search_with_my_profile'
              : 'search_with_my_skills',
          elementDetail: 'pill',
          componentName: 'ai_search',
          eventSchema: 'aicomponent',
          page: 'welcome/start/index',
        });

        navigate(event.currentTarget.href);
      }
    };

  const handleClickArrow = (direction: 'left' | 'right') => () => {
    if (!keywordsRef.current) {
      return;
    }

    const keywords = keywordsRef.current;
    const scrollAmount = keywords.clientWidth / 2;
    const newScrollLeft =
      direction === 'left'
        ? keywords.scrollLeft - scrollAmount
        : keywords.scrollLeft + scrollAmount;

    keywords.style.scrollBehavior = 'smooth';
    keywords.scrollLeft = newScrollLeft;
    keywords.style.scrollBehavior = 'auto';
  };

  useEffect(() => {
    const keywords = keywordsRef.current;

    // Scroll to the middle of the keywords and shuffle them
    if (keywords) {
      const scrollWidthHalf =
        keywords.scrollWidth / 2 - keywords.clientWidth / 2;

      keywords.scrollLeft = scrollWidthHalf;

      setKeywordsData({
        line1: shuffle(line1),
        line2: shuffle(line2),
      });
      setKeywordsPrepared(true);
    }
  }, [keywordsRef]);

  const salaryComponent = (
    <StyledLink
      draggable="false"
      href={getSuggestionUrl(FabiJobSearchQueryGenerationUseCase.EarnMore)}
      onClick={handleClick('swimp')}
      $isDragging={isDragging}
      key="JOBS_SEARCH_AI_SALARY_SUGGESTION"
    >
      <AiTag leadingIcon={IconSalary} hideBorder>
        <AiTagContent>
          <FormattedMessage id="JOBS_SEARCH_AI_SALARY_SUGGESTION" />
        </AiTagContent>
      </AiTag>
    </StyledLink>
  );
  const swimpComponent = (
    <StyledLink
      draggable="false"
      href={getSuggestionUrl(
        FabiJobSearchQueryGenerationUseCase.SearchWithProfile
      )}
      onClick={handleClick('swimp')}
      $isDragging={isDragging}
      key="JOBS_SEARCH_AI_SWIMP_SUGGESTION"
    >
      <AiTag leadingIcon={IconAIProfileSearch} hideBorder>
        <AiTagContent>
          <FormattedMessage id="JOBS_SEARCH_AI_SWIMP_SUGGESTION" />
        </AiTagContent>
      </AiTag>
    </StyledLink>
  );
  const swimsComponent = (
    <StyledLink
      draggable="false"
      href={getSuggestionUrl(
        FabiJobSearchQueryGenerationUseCase.SearchWithSkills
      )}
      onClick={handleClick('swims')}
      $isDragging={isDragging}
      key="JOBS_SEARCH_AI_SWIMS_SUGGESTION"
    >
      <AiTag leadingIcon={IconAICareerChanger} hideBorder>
        <AiTagContent>
          <FormattedMessage id="JOBS_SEARCH_AI_SWIMS_SUGGESTION" />
        </AiTagContent>
      </AiTag>
    </StyledLink>
  );

  return (
    <>
      <StyledLeftButton
        icon={IconArrowLeft}
        onClick={handleClickArrow('left')}
        aria-hidden
        tabIndex={-1}
      />
      <StyledRightButton
        icon={IconArrowRight}
        onClick={handleClickArrow('right')}
        aria-hidden
        tabIndex={-1}
      />
      <StyledKeywords
        data-qa="search-bar-keywords"
        ref={keywordsRef}
        onMouseDown={handleDragStart}
        onMouseUp={handleDragEnd}
        onMouseMove={handleDrag}
        onMouseLeave={handleDragEnd}
        $keywordsPrepared={keywordsPrepared}
        $isTouchDevice={isTouchDevice}
        className="is-light-theme"
      >
        <div>
          {keywordsData.line1.map(({ id, href }) => (
            <StyledLink
              draggable="false"
              href={href}
              $isDragging={isDragging}
              key={href}
            >
              <StyledTag hideBorder selected>
                <FormattedMessage id={id} />
              </StyledTag>
            </StyledLink>
          ))}
        </div>
        {swimpComponent}
        {salaryComponent}
        {swimsComponent}
        <div>
          {keywordsData.line2.map(({ id, href }) => (
            <StyledLink
              draggable="false"
              href={href}
              $isDragging={isDragging}
              key={href}
            >
              <StyledTag hideBorder selected>
                <FormattedMessage id={id} />
              </StyledTag>
            </StyledLink>
          ))}
        </div>
      </StyledKeywords>
    </>
  );
};
